<script lang="ts">
  import { onMount } from 'svelte'
  import { Icon, icons, Notification } from '@factry/components'
  import Header from '../components/Header.svelte'
  import ESG from '../tabs/ESG.svelte'
  import Overview from '../tabs/Overview.svelte'
  import Quality from '../tabs/Quality.svelte'
  import client from '../utils/client'
  import { lengthUOM, mapMeasurementsToDatasets, mapPeriodicDataToMeasurements, roundNumber } from '../utils/datasets'
  import type { AssetEvent, Dataset, MeasurementRecord, TabTypes } from '../types'

  export let uuid: string | undefined

  const tabs: TabTypes[] = ['Overview', 'Quality', 'ESG Metrics']

  let activeTab: TabTypes = 'Overview'
  let event: AssetEvent | undefined
  let length: number = 0
  let qualityDatasets: Dataset[] = []
  let thickness: number = 0

  async function getData (): Promise<void> {
    if (typeof uuid !== 'string' || !uuid.length) return

    try {
      let events: AssetEvent[] = []
      events = await client('/dpp/events', { params: { rowKey: uuid } })
      if (!Array.isArray(events) || !events.length) throw new Error('Could not load events')

      // We filtered for 1 event, so only use the first item of the array
      event = events[0]
      if (!event) throw new Error('Event data is missing')
      if (!event?.startTime || !event?.stopTime) throw new Error('Event doesn\'t have a start and stop time')

      // The co2 recycling data is currently not available (or hasn't been specified) from the historian, so add some mock data.
      event.conveyer_belt_co2_recycling = event.conveyer_belt_co2_emission_total * 0.2
      event.extruder_1_co2_recycling = event.extruder_1_co2_emission_total * 0.15
      event.gas_co2_recycling = event.gas_co2_emission_total * 0.03

      // On the event sometimes a number is sent as a string. Luckily in those cases it will have a <property>@odata.type property added to the data, so we can look for those and parse any strings.
      Object.keys(event).forEach(key => {
        const odataTypeKey = `${key}@odata.type`
        if (odataTypeKey in event! && typeof event![key] === 'string') {
          const parsedValue = odataTypeKey === 'Edm.Int64'
            ? parseInt(event![key])
            : parseFloat(event![key])
          if (!isNaN(parsedValue)) event![key] = parsedValue

          // Remove the <property>@odata.type properties now that we've parsed the data.
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete event![odataTypeKey]
        }
      })

      // Create measurements from the _periodic properties.
      let qualityMeasurements: MeasurementRecord[] = []
      const periodicKeys = Object.keys(event).filter(k => k.endsWith('_periodic'))
      // Split so we can make esg measurements separately if wanted.
      const qualityPeriodicKeys = periodicKeys.filter(k => !k.includes('_consumption'))

      qualityPeriodicKeys.forEach(key => {
        const eventMeasurements = mapPeriodicDataToMeasurements(event!, key)
        if (eventMeasurements) qualityMeasurements = [...qualityMeasurements, ...eventMeasurements]
      })

      // Construct the datasets here so we can use them for the graph and the min-max components. Datasets are the measurements grouped by PartitionKey and formatted for use by chart.js
      qualityDatasets = mapMeasurementsToDatasets(qualityMeasurements)

      setSummaryData()
    } catch (err) {
      Notification.error('Could not load event data', err)
    }
  }

  function setSummaryData (): void {
    const wallThicknessDataset = qualityDatasets.find(d => d.label === 'wall_thickness')
    const numericLength = wallThicknessDataset?.data.reduce((max, point) =>
      Math.max(max, parseFloat(`${point.x}`)), 0
    )
    length = Number(roundNumber(numericLength || 12.2))

    thickness = Number(roundNumber(event?.wall_thickness_mean || 25.4))
  }

  onMount(() => { void getData() })
</script>

<style lang="scss">
  @import '../utils/base';

  .wrapper {
    background-color: $white-ter;
    min-height: 100dvh;
  }

  .block-info {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: $spacing * 5;
  }

  summary {
    background-color: $white;
    border-bottom: 1px solid $border;
    padding: $spacing * 0.5;

    .title {
      margin-bottom: $spacing * 0.25;
    }

    p {
      color: $grey;
      display: flex;
      flex-wrap: wrap;

      &:not(:last-child) {
        margin-bottom: $spacing * 0.25;
      }
    }

    span {
      align-items: center;
      display: flex;
      margin-right: $spacing;

      @include from($tablet) {
        & ~ span:not(:empty)::before {
          background: $grey;
          border-radius: 100%;
          content: "";
          flex-shrink: 0;
          height: $spacing * 0.25;
          margin-right: $spacing;
          width: $spacing * 0.25;
        }
      }
    }
  }

  .tabs-wrapper {
    padding: $spacing * 0.5;
  }

  .tabs {
    background-color: $grey-lightest;
    border-radius: 100vw;
    padding: ($spacing * 0.125) ($spacing * 0.03);

    ul {
      justify-content: center;
    }

    li {
      padding: 0 ($spacing * 0.125);
      max-width: 350px;

      a {
        border-radius: 100vw;
        border: 0;
        padding: $spacing * 0.125;
        transition: all $speed * 3 ease-in-out;

        &:hover {
          background-color: $white;
          color: $black;
        }
      }

      &.is-active a {
        background-color: $white;
        color: $black;
      }
    }
  }
</style>

<div class="wrapper">
  <Header />

  <main>
    <summary>
      {#if event}
        <h1 class="title is-2">Certificate</h1>
        <p>
          <span>Recipe: {event.recipe}</span>
          {#if length}
            <span>Length: {length} {lengthUOM}</span>
          {/if}
          {#if thickness}
            <span>Wall Thickness: {thickness} {event?.wall_thickness_uom || ''}</span>
          {/if}
        </p>
        <p>Complete quality and ESG passport for this pipeline section</p>
      {:else}
        <div class="block-info">
          <Icon icon={icons.loading} />
          <span>Loading</span>
        </div>
      {/if}
    </summary>

    {#if event}
      <div class="tabs-wrapper">
        <div class="tabs is-toggle is-fullwidth">
          <ul>
            {#each tabs as tab}
              <!-- svelte-ignore a11y-missing-attribute -->
              <li
                class:is-active={tab === activeTab}
                on:click={() => (activeTab = tab)}
                on:keypress={(e) => e.key === 'Enter' && (activeTab = tab)}
              >
                <a>{tab}</a>
              </li>
            {/each}
          </ul>
        </div>
        <div class="content">
          {#if activeTab === 'Overview'}
            <Overview {event} {length} {thickness} />
          {/if}
          {#if activeTab === 'Quality'}
            <Quality datasets={qualityDatasets} {event} />
          {/if}
          {#if activeTab === 'ESG Metrics'}
            <ESG {event} {length} />
          {/if}
        </div>
      </div>
    {/if}
  </main>
</div>
